<!-- src/components/LoginScreen.vue -->
<template>
  <div class="login">
    <div class="login-brand">
      <img alt="Bronze Logo" class="logo" src="@/assets/logo.svg"/>
    </div>

    <!-- Boot / loading area. One state at a time. The movement is a contract:
         if something spins, the system is working; when it gives up, nothing moves. -->
    <div v-if="isLoading" class="boot" role="status" aria-live="polite">
      <!-- Working phases show the single spinner; the terminal 'failed' phase shows a
           static icon and never animates. -->
      <BzIcon v-if="bootPhase !== 'failed'" name="progress_activity" class="boot-icon boot-spinner bz-spinner" />
      <BzIcon v-else name="warning" class="boot-icon boot-error-icon" />

      <!-- connecting: one quiet line (pre-auth "Cargando…" or post-login "Conectando…") -->
      <div v-if="bootPhase === 'connecting'" class="boot-title">
        {{ connectingRestaurant ? $t('message.startupConnecting') : $t('message.loading') }}
      </div>

      <!-- slow: still retrying by itself. A live countdown proves it's automatic. No button. -->
      <template v-else-if="bootPhase === 'slow'">
        <div class="boot-title">{{ $t('message.loadingTakingLong') }}</div>
        <div class="boot-body">{{ $t('message.retryingAutomatically') }}</div>
        <div v-if="secondsToReload !== null" class="boot-meta">
          {{ $t('message.startupAutoReloadIn', { s: secondsToReload }) }}
        </div>
      </template>

      <!-- restarting: brief honest beat so the hard reload reads as intentional -->
      <div v-else-if="bootPhase === 'restarting'" class="boot-title">
        {{ $t('message.startupRestarting') }}
      </div>

      <!-- failed: terminal. Budget spent, nothing retries on its own. The ONLY
           boot state with a button, because tapping is the only thing left to do. -->
      <template v-else-if="bootPhase === 'failed'">
        <div class="boot-title">{{ $t('message.startupFailedTitle') }}</div>
        <div class="boot-body">{{ $t('message.startupFailedBody') }}</div>
        <BzButton class="retry-button" kind="primary" size="lg" block icon="refresh"
                  :label="$t('message.retryStartup')" @click="retryStartup"/>
        <div v-if="deviceId" class="boot-device-meta">
          {{ $t('message.startupDeviceMeta', { id: deviceId, version: appVersion }) }}
        </div>
        <!-- The way out when retrying cannot help (the session belongs to an id that no longer exists):
             staff could not reach Settings from here, so the only escape was clearing the browser's data. -->
        <BzButton class="boot-signout" data-testid="boot-signout" kind="text" size="md"
                  :label="$t('message.signOut')" @click="signOutFromStartup"/>
      </template>
    </div>

    <!-- Login form - only shown when not loading and login is required -->
    <div v-else class="login-form">
      <!-- The device id is lower case: it shows so while it is typed (CSS) and is stored so when
           the field is left. The keyboard is told not to start with a capital, not to correct and
           not to spell-check it: it is a code, not a word. -->
      <InputText
          id="deviceId"
          class="login-input login-input--code"
          :model-value="deviceId"
          :placeholder="$t('message.deviceId')"
          type="text"
          autocomplete="off"
          autocapitalize="none"
          autocorrect="off"
          spellcheck="false"
          enterkeyhint="next"
          @update:model-value="setDeviceId"
          @blur="normalizeDeviceId"
          @keydown.enter.prevent="focusOtpField"
      />
      <!-- One field, not PrimeVue's InputOtp: six boxes would change how the login looks, and the
           migration does not change the design. The code is a password the kitchen is given, not
           an SMS, so a single field loses nothing (docs/migracion-ui/mapa-componentes.md). -->
      <InputText
          id="otp"
          ref="otpField"
          class="login-input"
          :model-value="otp"
          :placeholder="$t('message.otpCode')"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          enterkeyhint="go"
          @update:model-value="setOtp"
          @keydown.enter="handleSignIn"
      />
      <!-- Why the form is here when nobody asked to sign out: the id this tablet was signed in with is gone. -->
      <div v-if="deviceIdChanged" class="login-note login-note--wrong" role="alert" data-testid="login-device-changed">
        <BzIcon name="warning" class="login-note__icon" /> {{ $t('message.deviceIdChanged') }}
      </div>
      <!-- Error sits next to its cause (right under the OTP field), not at the bottom. -->
      <div v-if="error" class="login-note login-note--wrong" role="alert">
        <BzIcon name="error" class="login-note__icon" /> {{ translateError(error) }}
      </div>
      <div class="button-group">
        <BzButton :disabled="isSigningIn" :class="{ 'is-busy': isSigningIn }" class="primary-button" kind="primary" size="lg" block
                  :icon="isSigningIn ? 'progress_activity' : undefined"
                  :label="isSigningIn ? $t('message.signingIn') : $t('message.signIn')" @click="handleSignIn"/>
        <BzButton class="help-button" data-testid="login-help" kind="outlined" size="md" block icon="help"
                  :label="$t('message.needHelp')" @click="openHelp"/>
      </div>
      <div v-if="helpSent" class="login-note login-note--ok" role="status" data-testid="login-help-sent">
        <BzIcon name="check_circle" class="login-note__icon" /> {{ $t('message.loginHelpSentToast') }}
      </div>
    </div>
  </div>
</template>
<script>
import {confirmSignIn, getCurrentUser, signIn, signOut, fetchAuthSession} from 'aws-amplify/auth';
import posthog from 'posthog-js';
import Logger from '@/logger';
import { withTimeout } from '@/utils';
import packageInfo from '../../package.json';
import { isMessengerReady } from '@/helpFlow';
import { composeLoginHelpMessage, readLastLogin, loginHelpAttribution } from '@/loginHelp';
import { focusIntercomComposer } from '@/intercomComposer';
import InputText from 'primevue/inputtext';
import BzButton from '@/ui/components/BzButton.vue';
import BzIcon from '@/ui/components/BzIcon.vue';

const logger = new Logger({screen: 'LoginScreen'});

// Product events for the manual login flow — the DB records successful logins
// (device.last_login) but is blind to failures and login friction, which is where
// support time goes. One event per attempt outcome, so volume stays negligible.
function trackLogin(event, props = {}) {
  try {
    posthog.capture(event, props);
  } catch (_) { /* never let telemetry throw */ }
}

// --- Startup watchdog --------------------------------------------------------
// While auto-logging-in or fetching the restaurant we show a boot state. These
// guard against a hang in the Cognito token refresh or the App.vue restaurant
// fetch leaving the rider stuck on a mute spinner forever.
const AUTH_TIMEOUT_MS = 12000;        // hard cap on each fetchAuthSession call
const SLOW_AFTER_MS = 8000;           // reveal the "taking too long" + auto-retry status
// Auto-reload only after the in-app retry budget (App.vue's 3×10s fetch attempts)
// has had a chance to self-heal — a smooth retry beats a jarring full reload.
const AUTO_RELOAD_AFTER_MS = 40000;
const MAX_AUTO_RELOADS = 2;           // ...but cap reloads to avoid a reload storm
const RESTART_HOLD_MS = 800;          // brief "restarting…" beat before the hard reload

export default {
  name: 'LoginScreen',
  components: { InputText, BzButton, BzIcon },
  props: {
    // Reported by App.vue: 'idle' | 'fetching' | 'failed' | 'device-gone' | 'ok'. A 'failed'
    // post-login fetch flips us straight to recovery without waiting out the timer; 'device-gone'
    // means the backend answered but knows no device by the signed-in id (renamed or deleted).
    startupStatus: {
      type: String,
      default: 'idle'
    }
  },
  data() {
    return {
      deviceId: '',
      otp: '',
      // Muestra "Enviado a soporte" bajo el botón de ayuda tras enviar el mensaje contextual;
      // sin esto el envío es invisible y la persona no sabe que ha funcionado.
      helpSent: false,
      helpSentTimer: null,
      error: '',
      // The signed-in id no longer exists: the form is back with that explanation above it.
      deviceIdChanged: false,
      message: '',
      isSigningIn: false,
      isLoading: true, // Start with loading state
      // Boot state machine (only meaningful while isLoading):
      // 'connecting' | 'slow' | 'restarting' | 'failed'
      bootPhase: 'connecting',
      // Switches the connecting copy from "Cargando…" to "Conectando con tu restaurante…"
      // once the OTP is accepted and App.vue is fetching the restaurant.
      connectingRestaurant: false,
      // Live seconds until the automatic reload, shown in the 'slow' state so the
      // rider can SEE that something is happening on its own.
      secondsToReload: null,
      appVersion: packageInfo.version,
    };
  },
  watch: {
    isLoading(nowLoading) {
      // Once we leave the loading state (form shown, or screen swapped) the
      // watchdog is no longer needed.
      if (!nowLoading) {
        this.clearStartupWatchdog();
        this.stopReloadCountdown();
        // No field is focused on its own: on Android that focus does not raise the keyboard, and the
        // tap that does raise it no longer brings the field into sight, so it stayed under the
        // keyboard (measured on the Honor, 30-sep-2026). A tap focuses it, and Chrome scrolls to it.
      }
    },
    startupStatus(status) {
      // App.vue's restaurant fetch resolved. On failure, don't sit through the slow
      // timer showing a false "retrying" message — decide now: reload if there's
      // still budget, otherwise surface the terminal failed state.
      if (status === 'failed' && this.isLoading) this.handleStartupFailure();
      else if (status === 'device-gone' && this.isLoading) this.leaveInvalidSession({ deviceIdChanged: true });
    }
  },
  beforeUnmount() {
    clearTimeout(this.helpSentTimer);
    this.clearStartupWatchdog();
    this.stopReloadCountdown();
    if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
  },
  async created() {
    if (__BRONZE_DEMO__) {
      // Demo mode: skip Cognito entirely and emit a fake successful login.
      const { demoUser, demoStopsAtLogin } = await import('@/demo');
      this.isLoading = false;
      if (demoStopsAtLogin()) return;
      this.$emit('login-success', demoUser);
      return;
    }
    const storedDeviceId = localStorage.getItem('device_id');
    const storedOtp = localStorage.getItem('otp');
    logger.debug(`User entered in the device screen`, {method: 'created'});

    if (storedDeviceId) {
      // Auto-login keeps us on the loading spinner — arm the watchdog now so a
      // hang anywhere in the chain (auth refresh or restaurant fetch) recovers.
      this.startStartupWatchdog();
      logger.identify(storedDeviceId);
      this.deviceId = storedDeviceId;
      logger.debug(`Stored device ID found ${storedDeviceId}`, {method: 'created'});

      // First check if we already have a valid session
      let hasValidSession = false;
      try {
        await this.checkUserRegistration();
        // If checkUserRegistration() succeeds, the user is already logged in and redirected
        hasValidSession = true;
      } catch (error) {
        logger.debug(`No valid session found, will try login with stored credentials if available`, {method: 'created', error});
        hasValidSession = false;
      }

      // Only attempt login with stored credentials if we don't have a valid session
      if (!hasValidSession && storedOtp) {
        logger.debug(`Stored OTP found, attempting automatic login`, {method: 'created'});
        this.otp = storedOtp;
        await this.handleSignIn({ automatic: true });
      } else if (!hasValidSession) {
        // If no valid session and no OTP, show the login form
        this.isLoading = false;
      }
    } else {
      logger.debug(`No stored device ID found`, {method: 'created'});
      // No stored device ID, show the login form
      this.isLoading = false;
    }
  },
  methods: {
    // Device id and OTP are typically copy-pasted or typed on a tablet, where
    // leading/trailing/inner spaces (and tab/newline characters from a paste)
    // creep in. Cognito matches both exactly, so any whitespace makes a valid
    // credential fail. Strip it as the user types so the bound value is clean.
    sanitizeDeviceId() {
      this.deviceId = this.deviceId.replace(/\s/g, '');
    },
    sanitizeOtp() {
      this.otp = this.otp.toString().replace(/\s/g, '');
    },
    // The fields are InputText: the value is written first and sanitized after, in one handler,
    // since with a component the order of `v-model` and `@input` is not guaranteed. And when the
    // clean value equals the model already (a space typed at the end), the prop would not change
    // and the field would keep showing the space: it travels through the raw value first.
    async setDeviceId(value) {
      const clean = value.replace(/\s/g, '');
      if (clean === this.deviceId && clean !== value) {
        this.deviceId = value;
        await this.$nextTick();
      }
      this.deviceId = clean;
    },
    // Lower case, but only once the field is left (or the form is sent), never key by key: on
    // Android the keyboard composes the word being typed, and rewriting the value under it breaks the
    // composition (letters doubled, the caret thrown to the end). While typing, CSS shows it lower case.
    normalizeDeviceId() {
      const lower = this.deviceId.toLowerCase();
      if (lower !== this.deviceId) this.deviceId = lower;
    },
    // «Siguiente» on the keyboard (Enter) goes on to the code instead of doing nothing. The focus moves
    // first: leaving the field is what lower-cases it (@blur), never with the keyboard still on it.
    // Without preventScroll: with the keyboard up, the scroll that comes with the focus is what
    // keeps the code field in sight above it.
    focusOtpField() {
      const el = this.$refs.otpField && (this.$refs.otpField.$el || this.$refs.otpField);
      if (el && typeof el.focus === 'function') el.focus();
    },
    async setOtp(value) {
      const raw = String(value);
      const clean = raw.replace(/\s/g, '');
      if (clean === this.otp && clean !== raw) {
        this.otp = raw;
        await this.$nextTick();
      }
      this.otp = clean;
    },
    // The OTP was accepted but startup isn't done: App.vue now fetches the
    // restaurant. Return to a truthful "connecting" boot state and (re)arm the
    // watchdog. Called on EVERY login-success path (auto and manual) — this is
    // what closes the manual-login gap where isLoading stayed false and a failed
    // fetch left the rider stuck with no recovery.
    enterConnectingState() {
      this.isSigningIn = false;
      this.error = '';
      this.connectingRestaurant = true;
      this.bootPhase = 'connecting';
      this.isLoading = true;
      this.startStartupWatchdog();
    },
    startStartupWatchdog() {
      this.clearStartupWatchdog();
      this._reloadDeadline = Date.now() + AUTO_RELOAD_AFTER_MS;
      this._slowTimer = setTimeout(() => {
        if (this.isLoading && this.bootPhase === 'connecting') {
          this.bootPhase = 'slow';
          this.startReloadCountdown();
          logger.warning('Startup taking too long — showing automatic-retry status', { method: 'startStartupWatchdog' });
        }
      }, SLOW_AFTER_MS);
      this._autoReloadTimer = setTimeout(() => {
        if (this.isLoading) this.handleStartupFailure();
      }, AUTO_RELOAD_AFTER_MS);
    },
    clearStartupWatchdog() {
      if (this._slowTimer) { clearTimeout(this._slowTimer); this._slowTimer = null; }
      if (this._autoReloadTimer) { clearTimeout(this._autoReloadTimer); this._autoReloadTimer = null; }
    },
    startReloadCountdown() {
      this.stopReloadCountdown();
      const tick = () => {
        this.secondsToReload = Math.max(0, Math.ceil((this._reloadDeadline - Date.now()) / 1000));
      };
      tick();
      this._countdownTimer = setInterval(tick, 1000);
    },
    stopReloadCountdown() {
      if (this._countdownTimer) { clearInterval(this._countdownTimer); this._countdownTimer = null; }
    },
    // Single decision point for "startup can't proceed" — fired both when the fetch
    // reports 'failed' and when the watchdog times out. Reload while budget remains
    // (a hard reload is the most reliable reset: re-runs Amplify config + auth +
    // fetch); once spent, stop and ask the rider to act.
    handleStartupFailure() {
      if (!this.isLoading) return;
      if (this.bootPhase === 'restarting' || this.bootPhase === 'failed') return; // already handling
      let count = 0;
      try { count = parseInt(sessionStorage.getItem('startup_reload_count') || '0', 10) || 0; } catch (_) { count = 0; }
      if (count < MAX_AUTO_RELOADS) {
        try { sessionStorage.setItem('startup_reload_count', String(count + 1)); } catch (_) { /* ignore */ }
        logger.error(`Startup stuck, auto-reloading (attempt ${count + 1}/${MAX_AUTO_RELOADS})`, { method: 'handleStartupFailure' });
        this.enterRestartingState();
      } else {
        logger.error('Startup still stuck after max auto-reloads, surfacing manual recovery', { method: 'handleStartupFailure' });
        this.stopReloadCountdown();
        this.clearStartupWatchdog();
        this.bootPhase = 'failed';
      }
    },
    enterRestartingState() {
      // Show a brief "restarting…" beat so the reload's white flash reads as
      // intentional rather than a crash, then do the hard reload.
      this.stopReloadCountdown();
      this.clearStartupWatchdog();
      this.bootPhase = 'restarting';
      this._restartTimer = setTimeout(() => { window.location.reload(); }, RESTART_HOLD_MS);
    },
    retryStartup() {
      // User tapped "Reintentar" on the terminal failed state. Parent handles the
      // reload (centralized, and resets the auto-reload budget) — a full reload
      // re-runs the whole bootstrap chain.
      logger.info('User tapped retry on startup failure', { method: 'retryStartup' });
      this.$emit('retry-startup');
    },
    // The session cannot work any more, and reloading will not change that: drop it and show the form.
    // The Cognito token of a renamed device stays valid up to a day, so without this the tablet kept
    // the old identity and ended on «Reintentar» with no way to sign out. Everything that names the
    // old id goes (Cognito tokens, stored id and code, the field) so the next login starts clean.
    async leaveInvalidSession({ deviceIdChanged = false } = {}) {
      this.stopReloadCountdown();
      this.clearStartupWatchdog();
      if (this._restartTimer) { clearTimeout(this._restartTimer); this._restartTimer = null; }
      try {
        await signOut();
      } catch (err) {
        logger.error('Error signing out of an invalid session', { error: err, method: 'leaveInvalidSession' });
      }
      this.clearStoredCredentials();
      try { sessionStorage.removeItem('startup_reload_count'); } catch (_) { /* ignore */ }
      this.deviceId = '';
      this.otp = '';
      this.error = '';
      this.helpSent = false;
      this.isSigningIn = false;
      this.connectingRestaurant = false;
      this.deviceIdChanged = deviceIdChanged;
      this.isLoading = false;
    },
    // «Cerrar sesión» on the failed boot screen: the same exit, chosen by staff. App.vue also clears
    // the local order data, as it does for the Settings sign-out.
    async signOutFromStartup() {
      logger.info('User signed out from the startup failure screen', { method: 'signOutFromStartup' });
      await this.leaveInvalidSession();
      this.$emit('sign-out');
    },
    async checkUserRegistration() {
      try {
        const user = await getCurrentUser();
        // Validate and refresh session if needed
        try {
          const session = await withTimeout(fetchAuthSession(), AUTH_TIMEOUT_MS, 'fetchAuthSession');
          // Verify we have valid tokens
          if (session?.tokens?.idToken && session?.tokens?.accessToken) {
            logger.debug(`Session validated successfully for user ${user.username}`, {method: 'checkUserRegistration'});
          } else {
            logger.warning(`Session exists but has no valid tokens`, {method: 'checkUserRegistration'});
            throw new Error('No valid tokens in session');
          }
        } catch (sessionError) {
          logger.warning(`Session validation failed, user needs to re-authenticate`, {error: sessionError, method: 'checkUserRegistration'});
          this.clearStoredCredentials(); // Clear credentials on session validation failure
          throw sessionError;
        }

        logger.debug(`User is already authenticated. username: ${user.username}, userId: ${user.userId}`, {method: 'checkUserRegistration'});
        if (user.username) {
          this.deviceId = user.username;
          localStorage.setItem('device_id', user.username);
          logger.debug(`User ${this.deviceId} is already logged in. Emitting 'login-success', parent will handle transition after loading settings`, {method: 'checkUserRegistration'});
          this.enterConnectingState();
          this.$emit('login-success', user);
          // Let parent (App.vue) handle transition to CameraScreen after settings are loaded
          return true;
        }
        return false;
      } catch (error) {
        if (error.name === 'TokenExpiredError' || error.name === 'NotAuthorizedException') {
          logger.debug('Session expired, clearing stored credentials', {error, method: 'checkUserRegistration'});
          this.clearStoredCredentials(); // Use the new method to clear both device_id and OTP
        } else {
          logger.debug('No current user, proceeding with sign up', {error, method: 'checkUserRegistration'});
        }
        throw error; // Re-throw the error to be caught in created()
      }
    },
    // `automatic`: the stored code, tried at start-up. When it fails the code is emptied: it is the
    // one that no longer works, and left in the field, what the kitchen types lands after it
    // («111111222222», another error). Enter and the button pass their event here, never `automatic`.
    async handleSignIn({ automatic = false } = {}) {
      this.normalizeDeviceId();
      this.isSigningIn = true;
      this.helpSent = false;
      try {
        logger.debug(`Attempting to sign in with device ID: ${this.deviceId}`, {method: 'handleSignIn'});
        const {nextStep} = await signIn({
          username: this.deviceId.trim(),
          options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP',
          },
        });
        // Only the step name: a custom-challenge nextStep carries additionalInfo from the auth service.
        logger.debug(`Sign in next step: ${nextStep?.signInStep}`, {method: 'handleSignIn'});
        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          try {
            const challengeResponse = this.otp.toString().replace(/\s/g, '');
            // The code is a credential: log that one was supplied, never its value.
            logger.debug('Confirming sign in with OTP (provided: true)', {method: 'handleSignIn'});
            await confirmSignIn({challengeResponse});

            // After successful sign in, explicitly fetch a fresh session
            await withTimeout(fetchAuthSession({ forceRefresh: true }), AUTH_TIMEOUT_MS, 'fetchAuthSession');
            logger.success('Sign in confirmed and session refreshed successfully');

            const user = await getCurrentUser();
            logger.console(`Current user after sign in: ${user?.username}`);

            this.message = this.$t('message.loginSuccessful');
            logger.debug(`User logged succesfully`, {method: 'handleSignIn'});
            trackLogin('login_success', { method: 'otp', deviceId: this.deviceId });

            // Store both device ID and OTP in local storage
            localStorage.setItem('device_id', this.deviceId);
            localStorage.setItem('otp', this.otp);

            // Move to the truthful "connecting" state + arm the watchdog, then let
            // App.vue drive the restaurant fetch and the transition to CameraScreen.
            this.enterConnectingState();
            this.$emit('login-success', user);
            logger.console('Login successful, parent will handle transition after loading settings');
          } catch (err) {
            logger.error(`User could not sign-in`, {error: err, method: 'handleSignIn'});
            trackLogin('login_failed', { reason: 'otp_rejected', errorName: err.name, deviceId: this.deviceId });
            this.error = `Could not sign-in: ${err.message}`;
            if (automatic) this.otp = '';
            this.isLoading = false; // Show login form on error
            this.isSigningIn = false;
          }
        }
      } catch (err) {
        logger.error(`Error during sign in`, {error: err});

        // Handle the case where there's already a signed in user
        if (err.message && err.message.includes('There is already a signed in user')) {
          logger.info('There is already a signed in user, retrieving current user and proceeding with login');

          try {
            // Get the current user and verify session
            const user = await getCurrentUser();

            // Verify we have a valid session with tokens
            const session = await withTimeout(fetchAuthSession(), AUTH_TIMEOUT_MS, 'fetchAuthSession');
            if (session?.tokens?.idToken && session?.tokens?.accessToken) {
              logger.success(`Retrieved existing user session for ${user.username}`);

              this.message = this.$t('message.loginSuccessful');
              this.deviceId = user.username;

              // Store device ID in local storage
              localStorage.setItem('device_id', this.deviceId);

              // Move to "connecting" + arm watchdog, then let parent handle transition.
              this.enterConnectingState();
              this.$emit('login-success', user);
              // Let parent (App.vue) handle transition to CameraScreen after settings are loaded
              return;
            } else {
              logger.warning('Session exists but has no valid tokens, user needs to re-authenticate');
              this.clearStoredCredentials();
              this.error = this.$t('message.sessionExpiredRelogin');
              this.isLoading = false; // Show login form
            }
          } catch (userError) {
            logger.error(`Error retrieving current user`, {error: userError});
            this.error = this.$t('message.errorOccurred');
            this.isLoading = false; // Show login form
          }
        } else {
          trackLogin('login_failed', { reason: 'sign_in_error', errorName: err.name, deviceId: this.deviceId });
          this.error = `Could not sign-in: ${err.message}`;
          if (automatic) this.otp = '';
          this.isLoading = false; // Show login form on error
        }
        this.isSigningIn = false;
      }
    },
    openHelp() {
      // A kitchen stuck here cannot use the in-app help flow — they are not logged in — and access
      // problems are the biggest support topic. So instead of opening an empty messenger, we send a
      // message that already carries what support would otherwise have to ask for: what they typed,
      // the error, and the last restaurant that logged in on this tablet. No composer to complete,
      // no send button to forget. Everything here is best-effort and must never throw into the tap.
      this.normalizeDeviceId(); // what support reads is what the login will send
      const ic = window.Intercom;
      if (!ic) {
        this.message = this.$t('message.loginHelpUnavailable');
        return;
      }
      let sent = false;
      try {
        const lastLogin = readLastLogin(window.localStorage);
        const attempt = { deviceId: this.deviceId, otp: this.otp };
        const message = composeLoginHelpMessage({
          attempt,
          lastLogin,
          appVersion: this.appVersion,
          error: this.error || null,
          t: (key, ...rest) => this.$t(key, ...rest),
        });

        // Attribute the conversation so support sees which restaurant is writing, even mid-failure:
        // the last confirmed login wins over what is being typed on a failing attempt.
        const userId = loginHelpAttribution({ typedDeviceId: this.deviceId, lastLogin });
        if (userId) {
          try { ic('update', { user_id: userId }); } catch (_) { /* attribution is a bonus */ }
        }

        // startConversation sends outright (verified on this workspace); showNewMessage only
        // pre-fills, so it is the fallback for when the widget has not fully loaded yet.
        if (isMessengerReady(ic)) {
          ic('startConversation', message);
          sent = true;
        } else {
          ic('showNewMessage', message);
          ic('show');
        }
        // Cursor en el redactor: quien no puede entrar suele querer añadir algo a mano.
        focusIntercomComposer();
      } catch (err) {
        logger.error('Login help failed to compose/send', { error: err, method: 'openHelp' });
        try { ic('show'); } catch (_) { /* last resort: at least open the messenger */ }
      }
      trackLogin('login_help_sent', {
        deviceId: this.deviceId || null,
        had_error: Boolean(this.error),
        had_history: Boolean(readLastLogin(window.localStorage)),
        sent,
      });
      if (sent) {
        this.helpSent = true;
        clearTimeout(this.helpSentTimer);
        this.helpSentTimer = setTimeout(() => { this.helpSent = false; }, 8000);
      }
    },
    translateError(error) {
      const errorMessages = {
        'Could not sign-in: Incorrect username or password.': this.$t('message.incorrectCredentials'),
        'Error during sign in: User not found.': this.$t('message.userNotFound'),
        'Error during sign in: Invalid OTP.': this.$t('message.invalidOTP'),
      };

      // Buscar coincidencias parciales para errores no mapeados
      for (const [eng, esp] of Object.entries(errorMessages)) {
        if (error.includes(eng)) return esp;
      }

      return this.$t('message.errorOccurred');
    },
    translateMessage(message) {
      const messages = {
        'Login successful!': this.$t('message.loginSuccessful'),
      };
      return messages[message] || message;
    },
    clearStoredCredentials() {
      localStorage.removeItem('device_id');
      localStorage.removeItem('otp');
      logger.debug('Cleared stored credentials due to authentication failure', {method: 'clearStoredCredentials'});
    },
  },
};
</script>

<style scoped>
/* The login is on the design system (docs/sistema-diseno/README.md): a centred column on the background,
   its fields and buttons the system's, and no brand colour yet: the yellow of the brand surfaces waits
   for the redesign of start-up, login and the news (docs/sistema-diseno/paleta-bronze.md). */
.login {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-8);
  box-sizing: border-box;
  min-height: 100vh;
  padding: var(--bz-gutter);
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
}

/* Fixed-height brand zone so the logo never jumps between states. */
.login-brand {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: var(--bz-target-primary);
}
.logo {
  width: 200px;
  max-width: 60vw;
}

/* Boot and form share a column (400 wide, inside the 360-420 of a login) and a minimum height so the
   composition above (logo) stays put as states swap. */
.boot,
.login-form {
  width: 100%;
  max-width: 400px;
  min-height: 288px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.login-form {
  align-items: stretch;
  gap: var(--bz-target-gap);
}
.boot {
  text-align: center;
  animation: login-fade var(--bz-duration-fast) var(--bz-ease-out);
}

.boot-icon {
  margin-bottom: var(--bz-space-6);
  font-size: var(--bz-icon-lg);
}
.boot-spinner { color: var(--bz-action-ink); }
.boot-error-icon { color: var(--bz-warn); }

.boot-title {
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
  text-wrap: balance;
}
.boot-body {
  margin-top: var(--bz-space-3);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  color: var(--bz-ink-2);
  text-wrap: balance;
}
.boot-meta {
  margin-top: var(--bz-space-4);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  color: var(--bz-ink-2);
  font-variant-numeric: tabular-nums;
}
.boot-device-meta {
  margin-top: var(--bz-space-5);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  color: var(--bz-ink-2);
  font-variant-numeric: tabular-nums;
}
.boot .retry-button {
  margin-top: var(--bz-space-6);
}
.boot .boot-signout {
  margin-top: var(--bz-space-4);
}

/* --- Form --- */
/* The fields are PrimeVue's InputText; everything they show is decided here: the control grey, a hairline
   edge, 56 high at the control radius, in the interface's letter at 16. `box-shadow: none` takes off the
   faint shadow Aura gives every field (what is flat is not lifted); the focus is the system's ring. */
.login-input {
  box-sizing: border-box;
  width: 100%;
  height: var(--bz-target-md);
  padding: 0 var(--bz-space-4);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-md);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  box-shadow: none;
}
.login-input:focus,
.login-input:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
  border-color: var(--bz-action-ink);
  box-shadow: none;
}
.login-input::placeholder {
  color: var(--bz-ink-2);
}
/* The device id reads in lower case from the first key (normalizeDeviceId stores it so); its
   placeholder keeps the copy's own case. */
.login-input--code {
  text-transform: lowercase;
}
.login-input--code::placeholder {
  text-transform: none;
}

/* The decision under the fields: 24 from the code to the main button, 12 between the two buttons. */
.button-group {
  display: flex;
  flex-direction: column;
  gap: var(--bz-target-gap);
  width: 100%;
  margin-top: var(--bz-target-gap);
}

/* The buttons are BzButton: their colours in every state are the preset's, their size the system's.
   "Busy" (signing in) is not "disabled": the button is disabled so it cannot be sent twice, but it keeps
   the action's full colour and label, and the movement of its icon says the system is working. */
.primary-button.is-busy:disabled {
  background: var(--bz-action);
  border-color: var(--bz-action);
  color: var(--bz-on-action);
  cursor: default;
}

.retry-button {
  max-width: 300px;
}

/* What the form says back, right under what caused it: a card of the state's container, with its icon
   and its words (norm 11). A failure is «incorrect»; the confirmation of the help message, «correct». */
.login-note {
  display: flex;
  align-items: flex-start;
  gap: var(--bz-space-2);
  box-sizing: border-box;
  width: 100%;
  padding: var(--bz-space-3) var(--bz-space-4);
  border-radius: var(--bz-radius-md);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-body);
  text-align: left;
  animation: login-fade var(--bz-duration-fast) var(--bz-ease-out);
}
.login-note--wrong {
  background: var(--bz-wrong-container);
  color: var(--bz-on-wrong-container);
}
.login-note--ok {
  background: var(--bz-ok-container);
  color: var(--bz-on-ok-container);
}
.login-note__icon {
  margin-top: 2px;
  font-size: var(--bz-icon-md);
}

/* Only a fade: opacity is the one thing that comes in here. */
@keyframes login-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* The busy button's icon is the system's spinner (src/ui/system.css): it turns while the app works on it,
   and with «remove animations» it holds still. BzButton draws the icon, so the class cannot go on it. */
@media (prefers-reduced-motion: no-preference) {
  .primary-button.is-busy :deep(.bz-icon) {
    animation: bz-spin 1s linear infinite;
  }
}
</style>
